import { deserializeFromMarkdown } from '@/tiptap/markdown/deserializeFromMarkdown'

function getSectionRange(
  doc: any,
  targetPos: number,
  targetEnd: number,
  node: any
): { from: number; to: number } {
  if (node.type.name !== 'heading') {
    return { from: targetPos, to: targetEnd }
  }

  const headingLevel = node.attrs?.level ?? 1
  let sectionEnd = targetEnd

  let pastTarget = false
  doc.forEach((child: any, offset: number) => {
    if (pastTarget) return
    const childStart = offset
    const childEnd = offset + child.nodeSize
    if (childStart < targetEnd) return

    if (
      child.type.name === 'heading' &&
      (child.attrs?.level ?? 1) <= headingLevel
    ) {
      sectionEnd = childStart
      pastTarget = true
      return
    }
    sectionEnd = childEnd
  })

  return { from: targetPos, to: sectionEnd }
}

export function applyBlockToEditor(
  editor: any,
  blockId: string,
  action: string,
  content?: string
) {
  const doc = editor.state.doc
  let targetPos: number | null = null
  let targetEnd: number | null = null
  let targetNode: any = null

  doc.descendants((node: any, pos: number) => {
    if (targetPos !== null) return false
    if (node.attrs?.id === blockId) {
      targetPos = pos
      targetEnd = pos + node.nodeSize
      targetNode = node
      return false
    }
  })

  if (targetPos === null || targetEnd === null || !targetNode) return

  // For insert-after targeting a HEADING, insert after the heading's whole
  // section (its paragraphs), not immediately after the heading line — the
  // agent means "add a new section after this section". For a paragraph
  // target, insert-after stays right after that block.
  const isHeadingTarget = targetNode.type?.name === 'heading'
  const useSectionRange =
    action === 'replace' ||
    action === 'delete' ||
    (action === 'insert-after' && isHeadingTarget)
  const range = useSectionRange
    ? getSectionRange(doc, targetPos, targetEnd, targetNode)
    : { from: targetPos, to: targetEnd }

  if (action === 'delete') {
    editor.chain().focus().deleteRange({ from: range.from, to: range.to }).run()
    return
  }

  if (action === 'replace' && content) {
    try {
      const { content: parsed } = deserializeFromMarkdown(content)
      const nodes = Array.isArray(parsed)
        ? parsed
        : (parsed?.content ?? [parsed])
      editor
        .chain()
        .focus()
        .insertContentAt({ from: range.from, to: range.to }, nodes)
        .run()
    } catch {
      editor
        .chain()
        .focus()
        .insertContentAt({ from: range.from, to: range.to }, content)
        .run()
    }
    return
  }

  if (action === 'insert-after' && content) {
    const insertAt = range.to
    try {
      const { content: parsed } = deserializeFromMarkdown(content)
      const nodes = Array.isArray(parsed)
        ? parsed
        : (parsed?.content ?? [parsed])
      editor.chain().focus().insertContentAt(insertAt, nodes).run()
    } catch {
      editor.chain().focus().insertContentAt(insertAt, content).run()
    }
  }
}
